import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Autocomplete,
  Stack,
  useTheme,
  useMediaQuery,
  Card,
  Button,
  ButtonGroup,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
// import { usePermission } from "../hooks/usePermission";
import Chart from "react-apexcharts";
import axios from "axios";
import config from "../config/config";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import vi from "date-fns/locale/vi";
import {
  startOfWeek,
  endOfWeek,
  startOfToday,
  endOfToday,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { styled } from "@mui/system";
import { ca } from "date-fns/locale";

// Styled Paper
const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const Dashboard = () => {
  const { token } = useAuth();
  // const { hasPermission } = usePermission();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalCustomers: 0,
    totalRevenue: 0,
    totalDebt: 0,
    totalProfit: 0,
  });

  const [revenueChartData, setRevenueChartData] = useState({
    options: {
      chart: { id: "revenue-chart", toolbar: { show: false } },
      xaxis: { categories: [], labels: { style: { colors: "#616161" } } },
      stroke: { curve: "smooth", width: 3 },
      title: {
        text: "Doanh thu theo thời gian",
        align: "left",
        style: { fontSize: "18px", fontWeight: 600, color: "#333" },
      },
      tooltip: { x: { format: "dd/MM/yy" } },
      grid: { borderColor: "#e0e0e0" },
      colors: ["#4CAF50"],
    },
    series: [{ name: "Doanh thu", data: [] }],
  });

  const [profitChartData, setProfitChartData] = useState({
    options: {
      chart: { id: "profit-chart", toolbar: { show: false } },
      xaxis: { categories: [], labels: { style: { colors: "#616161" } } },
      stroke: { curve: "smooth", width: 3 },
      title: {
        text: "Lợi nhuận theo thời gian",
        align: "left",
        style: { fontSize: "18px", fontWeight: 600, color: "#333" },
      },
      tooltip: { x: { format: "dd/MM/yy" } },
      grid: { borderColor: "#e0e0e0" },
      colors: ["#FF9800"],
    },
    series: [{ name: "Lợi nhuận", data: [] }],
  });

  // const [orderStatusChartData, setOrderStatusChartData] = useState({
  //   options: {
  //     chart: { id: "order-status-chart" },
  //     labels: [],
  //     responsive: [
  //       {
  //         breakpoint: 480,
  //         options: { chart: { width: 200 }, legend: { position: "bottom" } },
  //       },
  //     ],
  //     title: {
  //       text: "Trạng thái Đơn hàng",
  //       align: "left",
  //       style: { fontSize: "18px", fontWeight: 600, color: "#333" },
  //     },
  //     colors: ["#2196F3", "#FFC107", "#F44336", "#9C27B0"],
  //     legend: { position: "bottom" },
  //   },
  //   series: [],
  // });

  const [topProductsChartData, setTopProductsChartData] = useState({
    options: {
      chart: { id: "top-products-chart", toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          dataLabels: { position: "top" },
        },
      },
      dataLabels: {
        enabled: true,
        offsetX: -6,
        style: { fontSize: "12px", colors: ["#fff"] },
      },
      xaxis: { categories: [], labels: { style: { colors: "#616161" } } },
      title: {
        text: "Sản phẩm bán chạy",
        align: "left",
        style: { fontSize: "18px", fontWeight: 600, color: "#333" },
      },
      grid: { show: false },
      colors: ["#FF5722", "#00BCD4", "#8BC34A", "#FFEB3B", "#673AB7"],
    },
    series: [{ name: "Số lượng bán", data: [] }],
  });

  const [filters, setFilters] = useState(() => {
    const start = startOfToday();
    start.setHours(0, 0, 0, 0);
    const end = endOfToday();
    end.setHours(23, 59, 59, 999);
    return {
      startDate: start,
      endDate: end,
      productId: null,
      categoryId: null,
    };
  });

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [isFetchingProducts, setIsFetchingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const headers = { "x-auth-token": token };
        const categoriesRes = await axios.get(
          `${config.backendUrl}/api/categories`,
          { headers }
        );
        setCategories(categoriesRes.data.docs || []);
        fetchProducts(1, ''); // Fetch initial products
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };

    if (token) fetchInitialData();
    // eslint-disable-next-line
  }, [token]);

  const fetchProducts = async (page, search) => {
    if (isFetchingProducts) return;
    setIsFetchingProducts(true);
    try {
      const headers = { "x-auth-token": token };
      const res = await axios.get(`${config.backendUrl}/api/products`, {
        headers,
        params: { page, limit: 10, search },
      });
      const newProducts = res.data.docs || [];
      setProducts((prev) => (page === 1 ? newProducts : [...prev, ...newProducts]));
      setHasMoreProducts(res.data.hasNextPage);
      setProductPage(page + 1);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setIsFetchingProducts(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setProducts([]);
      fetchProducts(1, productSearch);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line
  }, [productSearch]);

  const fetchDashboardData = async () => {
    try {
      const headers = { "x-auth-token": token };
      const params = {
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
        productId: filters.productId,
        categoryId: filters.categoryId,
      };

      const statsRes = await axios.get(
        `${config.backendUrl}/api/dashboard/stats`,
        { headers, params }
      );
      setStats(statsRes.data);

      const profitRes = await axios.get(
        `${config.backendUrl}/api/dashboard/profit-over-time`,
        { headers, params }
      );
      setProfitChartData((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          xaxis: { categories: profitRes.data?.map((item) => item._id) || [] },
        },
        series: [
          {
            name: "Lợi nhuận",
            data: profitRes.data?.map((item) => item.dailyProfit) || [],
          },
        ],
      }));

      const revenueRes = await axios.get(
        `${config.backendUrl}/api/dashboard/revenue-over-time`,
        { headers, params }
      );
      setRevenueChartData((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          xaxis: { categories: revenueRes.data?.map((item) => item._id) || [] },
        },
        series: [
          {
            name: "Doanh thu",
            data: revenueRes.data?.map((item) => item.dailyRevenue) || [],
          },
        ],
      }));

      // const statusRes = await axios.get(
      //   `${config.backendUrl}/api/dashboard/order-status-distribution`,
      //   { headers, params }
      // );
      // setOrderStatusChartData((prev) => ({
      //   ...prev,
      //   options: {
      //     ...prev.options,
      //     labels: statusRes.data?.map((item) => item._id) || [],
      //   },
      //   series: statusRes.data?.map((item) => item.count) || [],
      // }));

      const topProductsRes = await axios.get(
        `${config.backendUrl}/api/dashboard/top-selling-products`,
        { headers, params }
      );
      setTopProductsChartData((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          xaxis: { categories: topProductsRes.data.map((item) => item.name) },
        },
        series: [
          {
            name: "Số lượng bán",
            data: topProductsRes.data.map((item) => item.totalSold),
          },
        ],
      }));
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
  };

  useEffect(() => {
    if (token) fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filters]);

  const handleFilterChange = (field, value) => {
    if (value) {
      if (field === "startDate") {
        value.setHours(0, 0, 0, 0);
      } else if (field === "endDate") {
        value.setHours(23, 59, 59, 999);
      }
    }
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateRangeChange = (range) => {
    let start, end;
    switch (range) {
      case "today":
        start = startOfToday();
        end = endOfToday();
        break;
      case "thisWeek":
        start = startOfWeek(new Date(), { weekStartsOn: 1 }); // Monday
        end = endOfWeek(new Date(), { weekStartsOn: 1 });
        break;
      case "thisMonth":
        start = startOfMonth(new Date());
        end = endOfMonth(new Date());
        break;
      default:
        return;
    }
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  // const handleApplyFilters = () => fetchDashboardData();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
      <Container maxWidth="lg">
        <Box sx={{ px: { xs: -20, sm: 3, md: 4 }, py: 3 }}>
          <Typography
            variant={isMobile ? "h6" : "h4"}
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, mt: isMobile ? -4 : 4 }}
          >
            Tổng quan
          </Typography>

          {/* Filters */}
          <StyledCard
            sx={{
              mb: 4,
              width: "100%",
              position: "static",
              top: isMobile ? "56px" : "auto",
              zIndex: 10,
              backgroundColor: "background.paper",
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              sx={{ mb: 2, alignItems: "center" }}
            >
              <ButtonGroup
                variant="outlined"
                aria-label="Date range filter"
                sx={{ flexShrink: 0 }}
              >
                <Button onClick={() => handleDateRangeChange("today")}>
                  Hôm nay
                </Button>
                <Button onClick={() => handleDateRangeChange("thisWeek")}>
                  Tuần này
                </Button>
                <Button onClick={() => handleDateRangeChange("thisMonth")}>
                  Tháng này
                </Button>
              </ButtonGroup>

              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                sx={{ width: "100%", margin: 0 }}
              >
                <DatePicker
                  label="Ngày bắt đầu"
                  format="dd/MM/yyyy"
                  value={filters.startDate}
                  onChange={(v) => handleFilterChange("startDate", v)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                  dayOfWeekFormatter={(day) => {
                    return ["CN", "T2", "T3", "T4", "T5", "T6", "T7"][
                        day.getDay()
                    ];
                  }}
                
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                sx={{ width: "100%", margin: 0 }}
              >
                <DatePicker
                  label="Ngày kết thúc"
                  value={filters.endDate}
                  onChange={(v) => handleFilterChange("endDate", v)}
                  format="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                  dayOfWeekFormatter={(day) => {
                    return ["CN", "T2", "T3", "T4", "T5", "T6", "T7"][
                        day.getDay()
                    ];
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                sx={{ width: "100%", margin: 0 }}
              >
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => option.name || ''}
                  onInputChange={(event, newInputValue) => {
                    setProductSearch(newInputValue);
                  }}
                  ListboxProps={{
                    onScroll: (event) => {
                      const listboxNode = event.currentTarget;
                      if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight && hasMoreProducts && !isFetchingProducts) {
                        fetchProducts(productPage, productSearch);
                      }
                    }
                  }}
                  onChange={(e, newVal) =>
                    handleFilterChange("productId", newVal?._id || null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Sản phẩm" fullWidth />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                sx={{ width: "100%", margin: 0 }}
              >
                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, newVal) =>
                    handleFilterChange("categoryId", newVal?._id || null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Danh mục" fullWidth />
                  )}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApplyFilters}
                  sx={{ py: 1.5, fontWeight: 600 }}
                >
                  Áp dụng
                </Button>
              </Grid> */}
            </Stack>
          </StyledCard>

          {/* Stats */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{ mb: 4, width: "100%" }}
          >
            {[
              {
                label: "Doanh thu",
                value: `${(stats.totalRevenue || 0).toLocaleString("vi-VN", {
                  style: "currency",
                  currency: "VND",
                })}`,
                color: "primary.main",
              },
              {
                label: "Lợi nhuận",
                value: `${(stats.totalProfit || 0).toLocaleString("vi-VN", {
                  style: "currency",
                  currency: "VND",
                })}`,
                color: "warning.main",
              },
              {
                label: "Công nợ",
                value: `${(stats.totalDebt || 0).toLocaleString("vi-VN", {
                  style: "currency",
                  currency: "VND",
                })}`,
                color: "error.main",
              },
              {
                label: "Sản phẩm bán được",
                value: stats.totalProducts,
                color: "secondary.main",
              },
              {
                label: "Đơn hàng",
                value: stats.totalOrders,
                color: "info.main",
              },
              {
                label: "Khách hàng",
                value: stats.totalCustomers,
                color: "success.main",
              },
            ].map((item, index) => (
              <Box xs={12} sm={6} md={3} key={index} sx={{ width: "100%" }}>
                <StyledCard>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: item.color }}
                  >
                    {item.value}
                  </Typography>
                </StyledCard>
              </Box>
            ))}
          </Stack>

          {/* Charts */}
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
            {/* <Grid item xs={12} md={4} sx={{ width: "40%" }}>
              <StyledCard>
                {orderStatusChartData && orderStatusChartData.series && orderStatusChartData.series.length > 0 ? (
                  <Chart
                    options={orderStatusChartData.options}
                    series={orderStatusChartData.series}
                    type="donut"
                    height={350}
                  />
                ) : (
                  <Typography>No order status data available</Typography>
                )}
              </StyledCard>
            </Grid> */}
            <Box xs={12} md={8} sx={{ width: "100%" }}>
              <StyledCard>
                {topProductsChartData &&
                topProductsChartData.series &&
                topProductsChartData.series[0].data.length > 0 ? (
                  <Chart
                    options={topProductsChartData.options}
                    series={topProductsChartData.series}
                    type="bar"
                    height={300}
                  />
                ) : (
                  <Typography>Không có dữ liệu sản phẩm bán chạy</Typography>
                )}
              </StyledCard>
            </Box>
          </Stack>

          <Box sx={{ mt: 4 }}>
            <StyledCard>
              {revenueChartData &&
              revenueChartData.series &&
              revenueChartData.series[0].data.length > 0 ? (
                <Chart
                  options={revenueChartData.options}
                  series={revenueChartData.series}
                  type="line"
                  height={350}
                />
              ) : (
                <Typography>Không có dữ liệu doanh thu</Typography>
              )}
            </StyledCard>
          </Box>

          <Box sx={{ mt: 4 }}>
            <StyledCard>
              {profitChartData &&
              profitChartData.series &&
              profitChartData.series[0].data.length > 0 ? (
                <Chart
                  options={profitChartData.options}
                  series={profitChartData.series}
                  type="line"
                  height={350}
                />
              ) : (
                <Typography>Không có dữ liệu lợi nhuận</Typography>
              )}
            </StyledCard>
          </Box>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default Dashboard;
